import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Palette, Type, Tag, Save, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

import BrandEditor from "@/components/workspace/BrandEditor";

const ACCENT_PRESETS = [
  { name: "cyan", label: "Neon Cyan", value: "#D4FF3A", hsl: "186 100% 50%" },
  { name: "violet", label: "Electric Violet", value: "#7000FF", hsl: "263 100% 50%" },
  { name: "emerald", label: "Emerald", value: "#00E676", hsl: "152 100% 45%" },
  { name: "amber", label: "Amber", value: "#FFAB00", hsl: "40 100% 50%" },
  { name: "ruby", label: "Ruby", value: "#FF1744", hsl: "349 100% 54%" },
  { name: "blue", label: "Royal Blue", value: "#2979FF", hsl: "219 100% 58%" },
];

const FONT_OPTIONS = [
  { name: "IBM Plex Sans", label: "IBM Plex Sans (Default)" },
  { name: "Inter", label: "Inter" },
  { name: "system-ui", label: "System Default" },
];

const DEFAULT_SETTINGS = { accentColor: "cyan", brandName: "MANAHOUSE", mode: "dark", fontFamily: "IBM Plex Sans" };

function loadSettings() {
  try {
    const saved = localStorage.getItem("manahouse-theme");
    return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function applyThemeSettings(settings) {
  const accent = ACCENT_PRESETS.find((c) => c.name === settings.accentColor);
  if (accent) {
    document.documentElement.style.setProperty("--primary", accent.hsl);
    document.documentElement.style.setProperty("--ring", accent.hsl);
  }
  document.documentElement.classList.remove("dark", "light");
  document.documentElement.classList.add(settings.mode);
  document.documentElement.style.setProperty("--font-body", settings.fontFamily);
}

export default function SettingsPage() {
  const [settings, setSettings] = useState(loadSettings);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    applyThemeSettings(settings);
  }, [settings]);

  const update = (key, value) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: value };
      return next;
    });
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem("manahouse-theme", JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
    localStorage.removeItem("manahouse-theme");
    applyThemeSettings(DEFAULT_SETTINGS);
    setSaved(false);
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider">SETTINGS</h1>
        <p className="text-sm text-muted-foreground mt-1">Rename this workspace, add branding, and customize the theme.</p>
      </div>

      <BrandEditor />

      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium flex items-center gap-2">
            <Palette className="w-4 h-4 text-neon-cyan" /> ACCENT COLOR
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {ACCENT_PRESETS.map((preset) => (
              <button
                key={preset.name}
                onClick={() => update("accentColor", preset.name)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2.5 rounded-sm border transition-all duration-200 min-h-[44px]",
                  settings.accentColor === preset.name
                    ? "border-white/30 bg-white/5 shadow-glow"
                    : "border-white/10 hover:border-white/20"
                )}
                data-testid={`accent-${preset.name}`}
              >
                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: preset.value }} />
                <span className="text-xs font-mono">{preset.label}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium flex items-center gap-2">
            <Tag className="w-4 h-4 text-neon-cyan" /> BRANDING
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Brand Name</Label>
            <Input
              value={settings.brandName}
              onChange={(e) => update("brandName", e.target.value)}
              className="mt-1.5 bg-white/5 border-white/10 rounded-sm"
              placeholder="e.g. UKIYOPROD"
              data-testid="brand-name-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1 font-mono">Displayed in sidebar and header</p>
          </div>
          <Separator className="bg-white/5" />
          <div className="flex items-center justify-between min-h-[44px]">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Dark Mode</Label>
              <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">Toggle between dark and light theme</p>
            </div>
            <Switch
              checked={settings.mode === "dark"}
              onCheckedChange={(v) => update("mode", v ? "dark" : "light")}
              data-testid="dark-mode-switch"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium flex items-center gap-2">
            <Type className="w-4 h-4 text-neon-cyan" /> TYPOGRAPHY
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {FONT_OPTIONS.map((f) => (
              <button
                key={f.name}
                onClick={() => update("fontFamily", f.name)}
                className={cn(
                  "px-3 py-2.5 rounded-sm border text-xs font-mono transition-all min-h-[44px]",
                  settings.fontFamily === f.name
                    ? "border-white/30 bg-white/5"
                    : "border-white/10 hover:border-white/20"
                )}
                style={{ fontFamily: f.name }}
                data-testid={`font-${f.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} className="rounded-sm bg-neon-cyan/90 text-black hover:bg-neon-cyan font-mono text-xs uppercase tracking-wider min-h-[44px] px-6" data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" /> {saved ? "Saved!" : "Save Settings"}
        </Button>
        <Button onClick={handleReset} variant="ghost" className="rounded-sm font-mono text-xs uppercase tracking-wider min-h-[44px]" data-testid="reset-settings-btn">
          <RotateCcw className="w-4 h-4 mr-2" /> Reset
        </Button>
      </div>
    </div>
  );
}
